import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import srOnly from 'styles/tools/sr-only'

const SectionContainer = styled.div`
  width: 100%;
  padding-top: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(32)};
  }
`

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
  min-height: ${rem(92)};
  padding: ${rem(16)};
  border-radius: ${rem(8)};
  background: linear-gradient(90deg, #304DAB 14.364%, #4C82DF 85.903%);
  color: ${props => props.theme.palette.neutral.surface.staticWhite};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    min-height: ${rem(56)};
    padding: ${rem(16)} ${rem(24)};
  }
`

const Logo = styled(LuxPlusLogo)`
  display: block;
  flex: 0 0 auto;
  width: ${rem(96)};
  height: auto;
  color: ${props => props.theme.palette.neutral.surface.staticWhite};

  ${mediaQueryUp.tablet} {
    width: ${rem(125)};
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 1;
    flex-direction: row;
    align-items: center;
    gap: ${rem(12)};
  }
`

const DesktopMessage = styled(BodyText)`
  display: none;
  margin: 0;
  min-width: 0;
  color: inherit;
  font-size: 20px;
  line-height: 24px;

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const MobileMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const MobileHeadline = styled(BodyText)`
  margin: 0;
  color: inherit;
  font-size: 16px;
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: 19px;
`

const MobileSupportingText = styled(BodyText)`
  margin: 0;
  color: inherit;
  font-size: 14px;
  line-height: 20px;
`

const BoldText = styled.strong`
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

const RegularText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

const HighlightText = styled.span`
  color: #00F1BD;
`

const ScreenReaderOnlyText = styled.span`
  ${srOnly}
`

function FlightsLuxPlusMemberBanner() {
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)

  return (
    <SectionContainer>
      <LayoutContainer variant="large">
        <Banner data-testid="lux-plus-flight-member-banner">
          <Content>
            <Logo colour="inherit" aria-hidden="true" />
            {canRedeemLuxPlusBenefits ? (
              <>
                <DesktopMessage as="p" variant="medium">
                  <FormattedMessage
                    id="JjajgAeDRHMxQH_alPG7"
                    defaultMessage="<bold>You've unlocked <highlight>member-only flight prices. </highlight></bold><regular>Search flights to see exclusive discounts.</regular>"
                    description="Member banner copy on the flights landing page confirming LuxPlus+ flight prices are unlocked"
                    values={{
                      bold: (chunks: ReactNode) => <BoldText>{chunks}</BoldText>,
                      highlight: (chunks: ReactNode) => <HighlightText>{chunks}</HighlightText>,
                      regular: (chunks: ReactNode) => <RegularText>{chunks}</RegularText>,
                    }}
                  />
                </DesktopMessage>
                <MobileMessage>
                  <MobileHeadline as="p" variant="medium">
                    <FormattedMessage
                      id="g0ycEBAuPPqcKA0lg8KM"
                      defaultMessage="You've unlocked <highlight>member-only prices.</highlight>"
                      description="Mobile headline for the member banner on the flights landing page confirming LuxPlus+ prices are unlocked"
                      values={{
                        highlight: (chunks: ReactNode) => <HighlightText>{chunks}</HighlightText>,
                      }}
                    />
                  </MobileHeadline>
                  <MobileSupportingText as="p" variant="medium">
                    <FormattedMessage
                      id="xCMrO_0x9F2-dtQwjovO"
                      defaultMessage="Search flights to see exclusive discounts."
                      description="Mobile supporting copy for the member banner on the flights landing page"
                    />
                  </MobileSupportingText>
                </MobileMessage>
              </>
            ) : (
              <>
                {/* Visible copy relies on the logo for "LuxPlus+"; keep a self-contained sentence for AT. */}
                <ScreenReaderOnlyText>
                  <FormattedMessage
                    id="Koca81ZuE9jn8Wy8Dq4r"
                    defaultMessage="LuxPlus+ members unlock exclusive flight prices"
                    description="Screen-reader-only non-member flights landing LuxPlus banner copy including brand name"
                  />
                </ScreenReaderOnlyText>
                <DesktopMessage as="p" variant="medium" aria-hidden="true">
                  <FormattedMessage
                    id="Yp6iWMyZRa6sd6OayAaG"
                    defaultMessage="<bold>members unlock <highlight>exclusive flight prices</highlight></bold>"
                    description="Non-member banner copy on the flights landing page next to the LuxPlus+ logo highlighting exclusive flight prices"
                    values={{
                      bold: (chunks: ReactNode) => <BoldText>{chunks}</BoldText>,
                      highlight: (chunks: ReactNode) => <HighlightText>{chunks}</HighlightText>,
                    }}
                  />
                </DesktopMessage>
                <MobileMessage aria-hidden="true">
                  <MobileHeadline as="p" variant="medium">
                    <FormattedMessage
                      id="L2LFQhCjg_1S2CRMzZp_"
                      defaultMessage="members unlock <highlight>exclusive flight prices</highlight>"
                      description="Mobile non-member banner copy on the flights landing page next to the LuxPlus+ logo highlighting exclusive flight prices"
                      values={{
                        highlight: (chunks: ReactNode) => <HighlightText>{chunks}</HighlightText>,
                      }}
                    />
                  </MobileHeadline>
                </MobileMessage>
              </>
            )}
          </Content>
        </Banner>
      </LayoutContainer>
    </SectionContainer>
  )
}

export default FlightsLuxPlusMemberBanner
